import { store } from './store'
import {
  authorize,
  exchange_origin_ticket,
  fetch_client_session,
  logout_client_session,
  persist_client_session,
  type AuthorizeInput,
  type AuthorizeResult,
  verify_auth,
} from './api'
import { init_recaptcha, get_recaptcha_token } from './recaptcha'
import { fetch_config } from './config'
import { can_bypass_driver_debug_auth, DRIVER_DEBUG_AUTH_TOKEN } from './driver-debug-config'
import { get_cached_geo_position, request_location_result, type GeoPosition } from './geo'
import { toast } from './toast'
import { reset_driver_session } from './driver-state'
import { api_url, is_native_app } from './runtime'
import { browser_session_storage } from './browser-storage'

const LOCAL_AUTH_BYPASS_TOKEN = 'dev-local-bypass'
const SESSION_CONTINUE_ATTEMPT_KEY_PREFIX = 'fidelidade_session_continue_attempted_'
const session_continue_attempts = new Set<'client' | 'driver'>()

function session_continue_attempt_key(flow: 'client' | 'driver'): string {
  return `${SESSION_CONTINUE_ATTEMPT_KEY_PREFIX}${flow}`
}

function session_continue_was_attempted(flow: 'client' | 'driver'): boolean {
  if (session_continue_attempts.has(flow)) return true
  try {
    return browser_session_storage.getItem(session_continue_attempt_key(flow)) === '1'
  } catch {
    return false
  }
}

function mark_session_continue_attempt(flow: 'client' | 'driver'): void {
  session_continue_attempts.add(flow)
  try {
    browser_session_storage.setItem(session_continue_attempt_key(flow), '1')
  } catch {
    // A trava em memória ainda impede repetição enquanto o documento existir.
  }
}

function clear_session_continue_attempt(flow: 'client' | 'driver'): void {
  session_continue_attempts.delete(flow)
  try {
    browser_session_storage.removeItem(session_continue_attempt_key(flow))
  } catch {
    // Storage pode estar indisponível em webviews restritas.
  }
}

export type SessionRestoreStatus =
  | 'not_checked'
  | 'cookie_restored'
  | 'cookie_saved'
  | 'cookie_missing'
  | 'cookie_invalid'
  | 'identity_invalid'
  | 'token_blocked'
  | 'flow_mismatch'
  | 'cnpj_mismatch'
  | 'request_failed'

let last_session_restore_status: SessionRestoreStatus = 'not_checked'

export function session_restore_status(): SessionRestoreStatus {
  return last_session_restore_status
}

async function fetch_session_with_transient_retry(flow: 'client' | 'driver') {
  try {
    return await fetch_client_session(flow)
  } catch {
    return fetch_client_session(flow)
  }
}

function store_authorize_result(result: AuthorizeResult): boolean {
  if (result.authorized && result.auth_token) {
    store.set('auth_token', result.auth_token)
    const extra = result
    if (extra.flow === 'driver') {
      if (extra.phone && store.get('driver_phone') !== extra.phone) {
        store.set('driver_ev_navigation', null)
      }
      store.set('driver_auth_token', result.auth_token)
      if (extra.phone) store.set('driver_phone', extra.phone)
    }
    if (extra.flow === 'client') {
      store.set('client_auth_token', result.auth_token)
      if (extra.phone) store.set('phone', extra.phone)
      if (extra.cnpj) store.set('cnpj', extra.cnpj)
    }
    return true
  }
  return false
}

async function persist_authorize_result(result: AuthorizeResult): Promise<boolean> {
  const stored = store_authorize_result(result)
  const flow = result.flow === 'client' || result.flow === 'driver' ? result.flow : null
  if (!stored || !flow || !result.auth_token) return stored
  clear_session_continue_attempt(flow)

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const persisted = await persist_client_session(result.auth_token, flow)
      if (!persisted) continue
      const confirmed = await fetch_client_session(flow)
      const identity_matches = confirmed.valid
        && confirmed.flow === flow
        && confirmed.auth_token === result.auth_token
        && confirmed.phone === result.phone
        && (flow === 'driver' || confirmed.cnpj === result.cnpj)
      if (identity_matches) {
        last_session_restore_status = 'cookie_saved'
        return true
      }
    } catch {
      // A sessão local continua válida nesta aba; a confirmação é refeita uma vez.
    }
  }

  last_session_restore_status = 'cookie_missing'
  return stored
}

function current_return_path(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

// `phone` precisa ser passado sempre que a sessão for de um telefone conhecido:
// o auth_token carrega esse telefone como claim assinado, e é dele que as rotas
// tiram o dono dos dados. Sem o claim, a sessão cai no fallback legado.
export async function run_captcha_flow(phone?: string): Promise<boolean> {
  // check_auth/run_captcha_flow são usados apenas nas rotas de motorista. O
  // fluxo e o retorno precisam seguir explícitos para o backend emitir o claim
  // escopado e o navegador conseguir confirmar o cookie correspondente.
  const identity = {
    ...(phone ? { phone } : {}),
    flow: 'driver' as const,
    return_path: current_return_path(),
  }

  if (can_bypass_driver_debug_auth()) {
    const result = await authorize({ recaptcha_token: DRIVER_DEBUG_AUTH_TOKEN, ...identity })
    return persist_authorize_result(result)
  }

  const config = await fetch_config().catch(() => null)
  if (!config) return false

  if (config.auth_mode === 'local-bypass') {
    const result = await authorize({ recaptcha_token: LOCAL_AUTH_BYPASS_TOKEN, ...identity })
    return persist_authorize_result(result)
  }

  if (!config.recaptcha_site_key) return false

  await init_recaptcha(config.recaptcha_site_key).catch(() => {})
  const recaptcha_token = await get_recaptcha_token('authorize')
  if (!recaptcha_token) return false

  const result = await authorize({ recaptcha_token, ...identity })
  return persist_authorize_result(result)
}

export async function check_auth(phone?: string): Promise<boolean> {
  const existing_token = store.get('auth_token')
  if (existing_token) {
    const result = await verify_auth(existing_token).catch(() => ({ valid: false }))
    if (result.valid) return true
    store.set('auth_token', null)
  }
  // Sem telefone explícito, usa o da sessão salva — assim o token re-emitido
  // volta com o claim em vez de cair no fallback legado.
  const session_phone = phone ?? store.get('driver_phone') ?? undefined
  if (can_bypass_driver_debug_auth()) return run_captcha_flow(session_phone)
  return false
}

export async function authorize_entry(input: Omit<AuthorizeInput, 'return_path'>): Promise<AuthorizeResult> {
  const result = await authorize({
    ...input,
    return_path: current_return_path(),
  })
  await persist_authorize_result(result)
  return result
}

async function existing_authorization_location(): Promise<GeoPosition | null> {
  const cached = get_cached_geo_position()
  if (cached) return cached

  // No navegador, o fallback de autorização não deve abrir um prompt implícito:
  // em quiet UI ele pode ficar pendente por até 20 s antes de seguir para a
  // validação protegida. Se a permissão já foi concedida, a localização continua
  // sendo aproveitada; caso contrário, o challenge segue imediatamente.
  if (!is_native_app()) {
    if (typeof navigator === 'undefined' || !navigator.permissions) return null
    try {
      const permission = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
      if (permission.state !== 'granted') return null
    } catch {
      return null
    }
  }

  return (await request_location_result(5000)).position
}

export async function authorize_entry_with_location_fallback(input: Omit<AuthorizeInput, 'return_path' | 'geo'>): Promise<boolean> {
  const first = await authorize_entry(input)
  if (store_authorize_result(first)) return true

  if (first.challenge_required && first.allow_browser_location) {
    const position = await existing_authorization_location()
    if (position) {
      const second = await authorize_entry({ ...input, geo: position })
      if (store_authorize_result(second)) return true
      if (second.validation_url) {
        window.location.assign(second.validation_url)
        return false
      }
      toast(second.error || 'Não foi possível validar sua origem.', 'warning')
      return false
    }

    if (first.validation_url) {
      window.location.assign(first.validation_url)
      return false
    }
  }

  if (first.validation_url) {
    window.location.assign(first.validation_url)
    return false
  }

  toast(first.error || 'Não foi possível validar sua origem.', 'warning')
  return false
}

export async function consume_origin_ticket_from_url(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search)
  const ticket = params.get('origin_ticket')
  if (!ticket) return false

  const result = await exchange_origin_ticket(ticket)
  if (!await persist_authorize_result(result)) {
    toast(result.error || 'Validação expirada. Tente novamente.', 'warning')
    return false
  }

  params.delete('origin_ticket')
  const search = params.toString()
  const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
  window.history.replaceState({}, '', next)
  return true
}

export async function restore_client_session(expected_cnpj?: string): Promise<boolean> {
  let result
  try {
    result = await fetch_session_with_transient_retry('client')
  } catch {
    last_session_restore_status = 'request_failed'
    return false
  }
  if (!result.valid || !result.auth_token || !result.phone || !result.cnpj || result.flow !== 'client') {
    last_session_restore_status = result.reason || (result.valid ? 'flow_mismatch' : 'cookie_invalid')
    return false
  }
  if (expected_cnpj && result.cnpj !== expected_cnpj) {
    last_session_restore_status = 'cnpj_mismatch'
    return false
  }
  const restored = store_authorize_result({
    authorized: true,
    auth_token: result.auth_token,
    phone: result.phone,
    cnpj: result.cnpj,
    flow: result.flow,
  })
  last_session_restore_status = restored ? 'cookie_restored' : 'cookie_invalid'
  if (restored) clear_session_continue_attempt('client')
  return restored
}

export async function restore_local_client_session(expected_cnpj?: string): Promise<boolean> {
  const auth_token = store.get('client_auth_token')
  const phone = store.get('phone')
  const cnpj = store.get('cnpj')

  // O token genérico é usado pelos fetches. Enquanto a identidade assinada não
  // for confirmada, mantê-lo ativo faria o guard atravessar a entrada do QR.
  store.set('auth_token', null)
  if (!auth_token || !phone || !cnpj) return false

  let result
  try {
    result = await verify_auth(auth_token)
  } catch {
    last_session_restore_status = 'request_failed'
    return false
  }

  const identity_matches = result.valid
    && result.flow === 'client'
    && result.phone === phone
    && result.cnpj === cnpj
    && (!expected_cnpj || result.cnpj === expected_cnpj)
  if (!identity_matches) {
    store.set('client_auth_token', null)
    last_session_restore_status = result.valid ? 'identity_invalid' : 'cookie_invalid'
    return false
  }

  store.set('auth_token', auth_token)
  last_session_restore_status = 'cookie_restored'
  return true
}

export async function restore_driver_session(): Promise<boolean> {
  let result
  try {
    result = await fetch_session_with_transient_retry('driver')
  } catch {
    last_session_restore_status = 'request_failed'
    return false
  }
  if (!result.valid || !result.auth_token || !result.phone || result.flow !== 'driver') {
    last_session_restore_status = result.reason || (result.valid ? 'flow_mismatch' : 'cookie_invalid')
    return false
  }
  const restored = store_authorize_result({
    authorized: true,
    auth_token: result.auth_token,
    phone: result.phone,
    flow: result.flow,
  })
  last_session_restore_status = restored ? 'cookie_restored' : 'cookie_invalid'
  if (restored) clear_session_continue_attempt('driver')
  return restored
}

export async function restore_local_driver_session(): Promise<boolean> {
  const driver_auth_token = store.get('driver_auth_token')
  const driver_phone = String(store.get('driver_phone') || '').replace(/\D/g, '')
  if (driver_auth_token && driver_phone) {
    store.set('auth_token', driver_auth_token)
    return true
  }

  // Atalhos v1 podiam deixar somente o token genérico no storage. Ele não pode
  // atravessar o guard moderno sem antes provar, no servidor, que pertence ao
  // mesmo motorista. Se estiver velho ou for de outra identidade, removê-lo
  // permite que a entrada refaça a autorização normal pelo telefone do PIN.
  const legacy_auth_token = store.get('auth_token')
  if (!legacy_auth_token || !driver_phone) return false
  try {
    const result = await verify_auth(legacy_auth_token)
    const result_phone = String(result.phone || '').replace(/\D/g, '')
    if (result.valid && result.flow === 'driver' && result_phone === driver_phone) {
      store.set('driver_auth_token', legacy_auth_token)
      last_session_restore_status = 'cookie_restored'
      return true
    }
    last_session_restore_status = result.valid ? 'identity_invalid' : 'cookie_invalid'
  } catch {
    last_session_restore_status = 'request_failed'
  }
  store.set('auth_token', null)
  return false
}

export async function restore_driver_access(): Promise<boolean> {
  if (await restore_local_driver_session()) return true
  return restore_driver_session()
}

export function current_driver_auth_token(): string | null {
  return store.get('driver_auth_token')
}

function submit_browser_session_navigation(flow: 'client' | 'driver', return_path: string): boolean {
  const auth_token = store.get(flow === 'client' ? 'client_auth_token' : 'driver_auth_token')
  if (!auth_token || is_native_app() || typeof document === 'undefined') return false
  if (session_continue_was_attempted(flow)) return false

  const form = document.createElement('form')
  form.method = 'POST'
  form.action = api_url('/api/client-session/continue')
  form.style.display = 'none'

  const session_input = document.createElement('input')
  session_input.type = 'hidden'
  session_input.name = 'session_token'
  session_input.value = auth_token
  form.appendChild(session_input)

  const flow_input = document.createElement('input')
  flow_input.type = 'hidden'
  flow_input.name = 'flow'
  flow_input.value = flow
  form.appendChild(flow_input)

  const return_input = document.createElement('input')
  return_input.type = 'hidden'
  return_input.name = 'return_path'
  return_input.value = return_path
  form.appendChild(return_input)

  document.body.appendChild(form)
  mark_session_continue_attempt(flow)
  try {
    form.submit()
  } catch (err) {
    clear_session_continue_attempt(flow)
    throw err
  }
  return true
}

export function submit_client_session_navigation(return_path: string): boolean {
  return submit_browser_session_navigation('client', return_path)
}

export function submit_driver_session_navigation(return_path: string): boolean {
  return submit_browser_session_navigation('driver', return_path)
}

export async function clear_client_session(): Promise<void> {
  await logout_client_session('client')
  clear_local_client_session()
  clear_session_continue_attempt('client')
  last_session_restore_status = 'cookie_missing'
}

function clear_local_client_session(): void {
  store.set('auth_token', null)
  store.set('client_auth_token', null)
  store.set('phone', null)
  store.set('voucher', null)
}

export async function invalidate_client_session(): Promise<void> {
  try {
    await logout_client_session('client')
  } catch {
    console.warn('[auth] não foi possível remover o cookie da sessão empresarial rejeitada')
  } finally {
    clear_local_client_session()
    clear_session_continue_attempt('client')
    last_session_restore_status = 'identity_invalid'
  }
}

export async function clear_driver_session(): Promise<void> {
  await logout_client_session('driver')
  reset_driver_session()
  clear_session_continue_attempt('driver')
  last_session_restore_status = 'cookie_missing'
}
